import axios, { AxiosError, InternalAxiosRequestConfig } from "axios"
import { store } from "../../reduxStore/store"
import {
  logoutSession,
  selectIsTokenExpired,
  selectUserToken,
  unauthenticated,
} from "./authenticationSlice"
import { appInsights } from "../logging/ApplicationInsightsService"
import appInsightsLogger from "../logging/appInsightsLogger"
import { checkAndRefreshToken } from "./tokenService"
import { getBaseEndpoint } from "../endpoints/endpointManager"
import { ProtectedConfig } from "../../domain/Account/schema"

const BASE_PATH: string = window._env_.AFFILIATE_API_BASE_ENDPOINT

// Extend the AxiosRequestConfig interface
declare module "axios" {
  export interface AxiosRequestConfig {
    _retry?: boolean
  }
}

// Get the token stored in the msal JWT
export const getAccessToken = (): string => {
  const state = store.getState()
  const token = selectUserToken(state)
  return token ? token : ""
}

export const requestInterceptor = async (
  config: InternalAxiosRequestConfig,
) => {
  // this is used for two instances, one with a base url set in the axios instance and one without
  if (config.url && (!!config.baseURL || config.url.startsWith(BASE_PATH))) {
    await checkAndRefreshToken()

    config.headers.set("Authorization", `Bearer ${getAccessToken()}`)
    config.headers.set(
      "veap-user-sessionid",
      appInsights.context.sessionManager.automaticSession.id,
    )
  }
  return config
}

export const getProtectedConfig = async (): Promise<ProtectedConfig> => {
  try {
    const protectedConfig = await axios.get(
      getBaseEndpoint() + "/account/config",
    )
    window._env_ = {
      ...window._env_,
      ConnectionStrings__ApplicationInsights:
        protectedConfig.data.app_insights_connection_string,
      AZURE_MAPS_SUBSCRIPTION_KEY:
        protectedConfig.data.azure_maps_subscription_key,
      MIGRATION_SERVICE_USER_ID: protectedConfig.data.migration_user_id,
    }
    return protectedConfig.data
  } catch (error) {
    throw new Error("Failed to get protected configuration " + error)
  }
}

export const requestErrorInterceptor = (error: Error) => {
  return Promise.reject(error)
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const responseInterceptor = (response: any) => response

export const responseErrorInterceptor = async (error: AxiosError) => {
  const originalRequest = error.config
  if (
    error.response?.status === 401 &&
    originalRequest &&
    !originalRequest._retry
  ) {
    originalRequest._retry = true

    // Check if the 401 is due to an expired token
    const isTokenExpired = selectIsTokenExpired(store.getState())

    if (isTokenExpired) {
      // Token is expired, let the request interceptor handle the refresh
      return axios(originalRequest)
      // we only want to log out on retry 401 if the request is to our APIM
    } else if (originalRequest.url?.startsWith(BASE_PATH)) {
      // Token is not expired, but we still got a 401. This indicates a different authentication issue.
      appInsightsLogger.traceError(
        "Received 401 with a valid token. Logging out...",
      )
      store.dispatch(unauthenticated())
      store.dispatch(logoutSession())
      return Promise.reject(error)
    }
  } else if (
    error.response?.status === 401 &&
    originalRequest &&
    originalRequest._retry &&
    originalRequest.url?.startsWith(BASE_PATH)
  ) {
    // Still 401, but we've already retried once. This indicates a different authentication issue.
    appInsightsLogger.traceError("Received 401 after retry. Logging out...")
    store.dispatch(unauthenticated())
    store.dispatch(logoutSession())
    return Promise.reject(error)
  }
  return Promise.reject(error)
}

export const setupAxiosInterceptors = () => {
  axios.interceptors.request.use(requestInterceptor, requestErrorInterceptor)
  axios.interceptors.response.use(responseInterceptor, responseErrorInterceptor)
}
